<script setup lang="ts">
import {computed, ref} from "vue";

type Variant = {
  id: string
  title: string
  description?: string
  thumbnail?: string
  price?: number
  isDefault?: boolean
}

const props = defineProps<{
  groupId: string
  groupTitle?: string
  groupDescription?: string
  variants?: Variant[]
  selectedId?: string
  /** Preis-Deltas relativ zur aktuellen Auswahl, Schlüssel = optionId */
  diffs?: Record<string, number>
  /** Währung für Formatierung (default EUR) */
  currency?: string
}>()
const hoverPreview = ref<{ url: string; x: number; y: number } | null>(null)

function showPreview(e: MouseEvent, url: string) {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  hoverPreview.value = {
    url,
    x: rect.left,               // Start X (Viewport)
    y: rect.bottom + 8          // knapp unter dem Thumbnail
  }
}
function hidePreview() {
  hoverPreview.value = null
}

const emit = defineEmits<{
  (e: 'selectVariant', variantId: string): void
  (e: 'close'): void
}>()

const nf = computed(
    () => new Intl.NumberFormat('de-DE', { style: 'currency', currency: props.currency || 'EUR' })
)

function selectVariant(variantId: string) {
  emit('selectVariant', variantId)
  emit('close')
}

function deltaFor(v: Variant) {
  if (v.id === props.selectedId) return undefined // nur Nicht-Ausgewählte zeigen
  const d = props.diffs?.[v.id]
  return typeof d === 'number' ? d : undefined
}
function deltaText(d?: number) {
  if (d == null) return ''
  const sign = d > 0 ? '+' : d < 0 ? '−' : '±'
  const abs = Math.abs(d)
  return `${sign}${nf.value.format(abs)}`
}
function deltaClass(d?: number) {
  if (d == null) return ''
  return d > 0 ? 'up' : d < 0 ? 'down' : 'flat'
}

// Tastatursteuerung: ↑/↓ navigieren, Enter/Space wählen
function onKeydown(e: KeyboardEvent, id: string) {
  const items = Array.from(
      (e.currentTarget as HTMLElement)?.closest('.variant-list')?.querySelectorAll<HTMLElement>('.variant-item') || []
  )
  const idx = items.findIndex(el => el === e.currentTarget)
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    const next = items[(idx + 1) % items.length]
    next?.focus()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    const prev = items[(idx - 1 + items.length) % items.length]
    prev?.focus()
  } else if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    selectVariant(id)
  }
}
</script>

<template>
  <div class="placeholder-group">
    <div class="group-header">
      <p v-if="groupDescription" class="group-desc">{{ groupDescription }}</p>
    </div>

    <ul class="variant-list" role="listbox" :aria-label="groupTitle || 'Varianten'">
      <li
          v-for="v in variants"
          :key="v.id"
          class="variant-item"
          :class="{ selected: v.id === selectedId }"
          role="option"
          :aria-selected="v.id === selectedId"
          tabindex="0"
          @click="selectVariant(v.id)"
          @keydown="onKeydown($event, v.id)"
      >
        <div class="variant-main">
          <div class="variant-titleline">
            <div
                v-if="v.thumbnail"
                class="variant-thumb-wrapper"
                @mouseenter="showPreview($event, v.thumbnail)"
                @mouseleave="hidePreview"
            >
              <img :src="v.thumbnail" alt="" class="variant-thumb" />
            </div>

            <!-- Globaler Tooltip -->
            <Teleport to="body">
              <div
                  v-if="hoverPreview"
                  class="variant-tooltip-global"
                  :style="{ left: hoverPreview.x + 'px', top: hoverPreview.y + 'px' }"
              >
                <img :src="hoverPreview.url" alt="" />
              </div>
            </Teleport>

            <strong class="v-title">{{ v.title }}</strong>
            <svg v-if="v.id === selectedId" class="check" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>

          <p v-if="v.description" class="variant-desc">{{ v.description }}</p>
        </div>

        <div class="variant-side">
          <div v-if="typeof v.price === 'number'" class="variant-price">
            {{ nf.format(v.price) }}
          </div>
          <div
              v-if="deltaFor(v) !== undefined"
              class="variant-delta"
              :class="deltaClass(deltaFor(v)!)"
              :title="'Preisänderung relativ zur aktuellen Auswahl'"
          >
            {{ deltaText(deltaFor(v)!) }}
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
/* Wrapper */
.placeholder-group {
  --bg: #fff;
  --surface: #fff;
  --surface-2: #fafafa;
  --border: #ececec;
  --border-2: #e6e6e6;

  --text: #141414;
  --muted: #6f6f6f;

  --radius: 16px;
  --radius-sm: 12px;

  --shadow: 0 12px 30px rgba(0,0,0,.08);
  --shadow-soft: 0 8px 20px rgba(0,0,0,.06);

  --brand: #e30b17;

  color: var(--text);
}

/* Header */
.group-header {
  margin: 0 0 14px 0;
}

.group-title {
  margin: 0 0 10px 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.15;
}

.group-desc {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.35;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}

/* List */
.variant-list {
  list-style: none;
  padding: 0;
  margin: 14px 0 0;
  display: grid;
  gap: 10px;
}

/* Item (Row Card) */
.variant-item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  align-items: center;

  padding: 12px 12px;
  border: 1px solid var(--border-2);
  background: var(--surface);

  cursor: pointer;
  user-select: none;

  transition: background 0.15s ease, border-color 0.15s ease, transform 0.08s ease, box-shadow 0.15s ease;
  outline: none;
}

.variant-item:hover {
  background: var(--surface-2);
  transform: translateY(-1px);
}

.variant-item:active {
  transform: translateY(0);
}

/* Keyboard */
.variant-item:focus-visible {
  outline: 3px solid rgba(227, 11, 23, 0.22);
  outline-offset: 2px;
}

/* Selected state (flat, sauber) */
.variant-item.selected {
  background: rgba(227, 11, 23, 0.04);
  border-color: rgba(227, 11, 23, 0.25);
}

/* Left content */
.variant-main {
  min-width: 0;
}

.variant-titleline {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.v-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.15;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Check */
.check {
  width: 18px;
  height: 18px;
  color: #2f8f2f;
  flex: 0 0 auto;
}

/* Description */
.variant-desc {
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.25;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Right side */
.variant-side {
  display: grid;
  justify-items: end;
  gap: 6px;
}

/* Price: ruhig, aber klar */
.variant-price {
  font-weight: 700;
  color: var(--text);
  font-size: 14px;
}

/* Delta als Chip */
.variant-delta {
  font-weight: 700;
  font-size: 12px;
  border-radius: 999px;
  background: #fff;
  line-height: 1;
  white-space: nowrap;
}


.variant-delta.up {
  color: #b70a12;
  border-color: rgba(227, 11, 23, 0.25);
  background: rgba(227, 11, 23, 0.06);
}

.variant-delta.down {
  color: #1f7a33;
  border-color: rgba(47, 143, 47, 0.25);
  background: rgba(47, 143, 47, 0.07);
}

.variant-delta.flat {
  color: #666;
  border-color: rgba(0,0,0,0.10);
  background: rgba(0,0,0,0.03);
}

/* Thumbnail */
.variant-thumb-wrapper {
  position: relative;
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  z-index: 3;
}

.variant-thumb {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid var(--border);
  background: #f5f5f5;
  box-shadow: 0 1px 0 rgba(0,0,0,0.03);
  cursor: zoom-in;
}

/* Global preview tooltip (Teleport) */
.variant-tooltip-global {
  position: fixed;
  padding: 8px;
  border-radius: 14px;
  background: rgba(255,255,255,0.96);
  border: 1px solid rgba(0,0,0,0.10);
  box-shadow: var(--shadow);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 2147483647;
}

.variant-tooltip-global img {
  width: 260px;         /* konstant -> pixel */
  height: 260px;
  object-fit: contain;
  display: block;
  border-radius: 10px;
  background: #fff;
}

/* Optional: etwas mehr Dichte auf kleinen Screens */
@media (max-width: 420px) {
  .variant-item {
    padding: 10px 10px;
    border-radius: 14px;
  }
  .variant-thumb {
    width: 42px;
    height: 42px;
  }
  .variant-tooltip-global img {
    width: 220px;
    height: 220px;
  }
}

</style>
